import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Update } from '@ngrx/entity';
import { Store } from '@ngrx/store';
import {
  getUnitConverterForParameter,
  makeDeviceParameterId,
  MotionMasterMessage,
  unitConverterParameters,
} from 'motion-master-client';
import { forkJoin, of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, takeWhile, tap, withLatestFrom } from 'rxjs/operators';

import {
  assignFeaturesToParameters,
  assignParametersUnitsFromUnitConverter,
  clearDeviceParametersInterfaceUnits,
  copyTuningGains,
  bulkDisableFirmwareLoggingRequest,
  bulkDisableFirmwareLoggingStatus,
  bulkEnableFirmwareLoggingRequest,
  bulkEnableFirmwareLoggingStatus,
  disableFirmwareLoggingRequest,
  disableFirmwareLoggingStatus,
  enableFirmwareLoggingRequest,
  enableFirmwareLoggingStatus,
  fetchDeviceInterfaceUnit,
  getDeviceParameterInfoRequest,
  getDeviceParameterInfoStatus,
  getDeviceParameterValuesRequest,
  getDeviceParameterValuesStatus,
  loadDeviceParameterValues,
  setDeviceParametersInterfaceUnitsFromUiConfig,
  setDeviceParameterValuesRequest,
  setDeviceParameterValuesStatus,
  updateDeviceParameters,
  deviceParameterValuesLoaded,
} from './device-parameter.actions';
import { DeviceParameter, deviceParameterIdParts } from './device-parameter.model';
import { reloadDeviceStateMachineRequest, resetDeviceFaultRequest } from './device.actions';
import { getParameterValue, mapParameterToValueChanges, Parameter } from './parameter.model';

import { AppState, selectAllDeviceParameters } from './index';

import { mapDeviceParameterValuesChanges } from '@app/custom-operators';
import { deviceParameterValuesCompleted, deviceParameterValuesRunning } from '@app/models/status-resolve';
import { ConnectionService } from '@app/services/connection.service';
import { DeviceService } from '@app/services/device.service';
import { GlobalService } from '@app/services/global.service';
import { getCurrentValue, getInterfaceUnitFromUnitConverter } from '@app/util';

@Injectable()
export class DeviceParameterEffects {
  private actions$ = inject(Actions);
  private store$ = inject<Store<AppState>>(Store);
  private cn = inject(ConnectionService);
  private deviceService = inject(DeviceService);
  private globalService = inject(GlobalService);

  handleCopyTuningGains$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(copyTuningGains),
        withLatestFrom(this.store$),
        map(([action, state]) => {
          const { device, velocityThreshold } = action;
          const parameters = selectAllDeviceParameters(state);
          const positionControllerParameters = parameters.filter(
            (p) => p.index === 0x2012 && p.deviceSerialNumber === device.serialNumber,
          );
          const deviceParameterValues = positionControllerParameters.slice(1, 9).reduce((acc, p) => {
            acc[makeDeviceParameterId(device.serialNumber, 0x2013, p.subindex + (velocityThreshold === 0 ? 2 : 11))] =
              p.value;
            return acc;
          }, Object.create(null));
          this.deviceService.requestSetDeviceParameterValues(deviceParameterValues);
        }),
      ),
    { dispatch: false },
  );

  handleGetDeviceParameterInfoRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getDeviceParameterInfoRequest),
      mergeMap((action) => {
        const { device, suppressToastr } = action;

        return this.cn.client.request
          .getDeviceParameterInfo({ deviceAddress: device.deviceAddress }, action.timeout)
          .pipe(
            map((deviceParameterInfo) => getDeviceParameterInfoStatus({ device, deviceParameterInfo })),
            catchError(() => {
              const deviceName = this.deviceService.getDeviceName(device);

              return of(
                getDeviceParameterInfoStatus({
                  device,
                  deviceParameterInfo: {
                    timeout: {
                      message: `Timeout while getting the parameter info on ${deviceName}.`,
                    },
                  },
                  suppressToastr,
                }),
              );
            }),
          );
      }),
    ),
  );

  handleGetDeviceParameterValuesRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getDeviceParameterValuesRequest),
      mergeMap((action) => {
        const { device, parameters, sendProgress, initiator, suppressToastr } = action;
        return this.cn.client.request
          .getDeviceParameterValues({ deviceAddress: device.deviceAddress, parameters, sendProgress }, action.timeout)
          .pipe(
            takeWhile(deviceParameterValuesRunning, true),
            map((deviceParameterValues) =>
              getDeviceParameterValuesStatus({
                device,
                deviceParameterValues,
                initiator,
                suppressToastr,
              }),
            ),
            catchError(() => {
              const deviceName = this.deviceService.getDeviceName(device);
              return of(
                getDeviceParameterValuesStatus({
                  device,
                  deviceParameterValues: {
                    timeout: {
                      message: `Timeout while getting the parameter values from ${deviceName}.`,
                    },
                  },
                  initiator,
                  suppressToastr,
                }),
              );
            }),
          );
      }),
    ),
  );

  dispatchDeviceParametersLoadedOnGetDeviceParameterValuesStatusCompleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getDeviceParameterValuesStatus),
      filter((action) => deviceParameterValuesCompleted(action.deviceParameterValues)),
      filter((action) => action.initiator === loadDeviceParameterValues.type),
      map(({ device }) => deviceParameterValuesLoaded({ device })),
    ),
  );

  updateDeviceParametersOnGetDeviceParameterValuesStatusCompleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getDeviceParameterValuesStatus),
      filter((action) => deviceParameterValuesCompleted(action.deviceParameterValues)),
      withLatestFrom(this.store$),
      map(([action, state]) => {
        const { device, deviceParameterValues, initiator } = action;

        const updates: Update<DeviceParameter>[] = deviceParameterValues.parameterValues.flatMap(
          (parameter): Update<DeviceParameter>[] => {
            const id = makeDeviceParameterId(device.serialNumber, parameter);
            const deviceParameter = state.deviceParameters.entities[id];
            // The parameter catalog for this device may be incomplete or missing altogether, e.g. when
            // getDeviceParameters failed and the device was added with an empty catalog. Without an entity
            // in the store there is nothing to update, so skip the value instead of dereferencing undefined.
            if (!deviceParameter) {
              console.warn(`The received parameter value for ${id} has no matching parameter in the store.`);
              return [];
            }
            if (parameter.error) {
              console.warn(
                `The received parameter value for ${deviceParameter.id} has an error: ${parameter.error.code} ${MotionMasterMessage.Status.DeviceParameterValues.ParameterValue.Error.Code[parameter.error.code]} (${parameter.error.message}) `,
              );
              return [
                {
                  changes: {
                    error: parameter.error,
                  },
                  id: deviceParameter.id,
                },
              ];
            } else {
              return [
                {
                  changes: mapParameterToValueChanges(parameter as Parameter, getParameterValue(parameter)),
                  id: deviceParameter.id,
                },
              ];
            }
          },
        );
        return updateDeviceParameters({ device, initiator, updates });
      }),
    ),
  );

  handleSetDeviceParameterValuesRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setDeviceParameterValuesRequest),
      withLatestFrom(this.store$),
      mapDeviceParameterValuesChanges(),
      mergeMap(({ action, deviceToParameterValueChangesMap }) => {
        const { suppressToastr } = action;
        const device = deviceToParameterValueChangesMap.keys().next().value;
        const parameterValues = deviceToParameterValueChangesMap.get(device);
        return this.cn.client.request
          .setDeviceParameterValues({ deviceAddress: device.deviceAddress, parameterValues }, action.timeout)
          .pipe(
            map((deviceParameterValues) =>
              setDeviceParameterValuesStatus({ device, deviceParameterValues, suppressToastr }),
            ),
            catchError(() => {
              const deviceName = this.deviceService.getDeviceName(device);

              return of(
                setDeviceParameterValuesStatus({
                  device,
                  deviceParameterValues: {
                    timeout: {
                      message: `Timeout while setting the device parameter values on ${deviceName}.`,
                    },
                  },
                  suppressToastr,
                }),
              );
            }),
          );
      }),
    ),
  );

  handleSetDeviceParameterValuesStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setDeviceParameterValuesStatus),
      filter((action) => {
        if (action.deviceParameterValues?.parameterValues) {
          return true;
        } else {
          console.warn('No device parameter values in set device parameter values status.');
          return false;
        }
      }),
      tap((action) => {
        const { deviceParameterValues, suppressToastr } = action;
        if (deviceParameterValues.parameterValues.every((p) => !!p.success)) {
          this.globalService.toastrStatusMessage(deviceParameterValues, 'Set Device Parameter Values', suppressToastr);
        } else {
          console.warn(deviceParameterValues);
          this.globalService.toastrStatusMessage(
            { ...deviceParameterValues, error: { message: 'Unable to set values of some parameters.' } },
            'Set Device Parameter Values',
            suppressToastr,
          );
        }
      }),
      map((action) => {
        const { device, deviceParameterValues } = action;
        const parameterValues = deviceParameterValues.parameterValues.filter(
          (parameterValue) => !!parameterValue.success,
        );

        const updates: Update<DeviceParameter>[] = parameterValues.map((parameterValue) => {
          const id = makeDeviceParameterId(device.serialNumber, parameterValue);
          const typeValue = (parameterValue as any).typeValue;
          return {
            changes: {
              [typeValue]: parameterValue[typeValue],
              value: parameterValue[typeValue],
            },
            id,
          };
        });

        return updateDeviceParameters({ updates, initiator: action.type });
      }),
    ),
  );

  handleEnableFirmwareLoggingRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(enableFirmwareLoggingRequest),
      withLatestFrom(this.store$),
      mergeMap(([action, state]) => {
        const { device, showDeviceName } = action;
        const id = makeDeviceParameterId(device.serialNumber, 0x200e, 1);
        const deviceParameter = state.deviceParameters.entities[id];
        const typeValue = deviceParameter?.typeValue ?? 'int32Value';
        const parameterValues = [{ index: 0x200e, subindex: 1, [typeValue]: 1 }];
        return this.cn.client.request
          .setDeviceParameterValues({ deviceAddress: device.deviceAddress, parameterValues }, 5000)
          .pipe(
            map((deviceParameterValues) =>
              enableFirmwareLoggingStatus({ device, deviceParameterValues, showDeviceName }),
            ),
            catchError(() => {
              const deviceName = this.deviceService.getDeviceName(device);
              return of(
                enableFirmwareLoggingStatus({
                  device,
                  showDeviceName,
                  deviceParameterValues: {
                    timeout: { message: `Timeout while enabling firmware logging on ${deviceName}.` },
                  },
                }),
              );
            }),
          );
      }),
    ),
  );

  handleEnableFirmwareLoggingStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(enableFirmwareLoggingStatus),
      tap((action) => {
        const { device, deviceParameterValues, showDeviceName } = action;
        const deviceName = showDeviceName ? this.deviceService.getDeviceName(device) : undefined;
        if (deviceParameterValues.parameterValues?.every((p) => !!p.success)) {
          const message = deviceName
            ? `Firmware logging enabled on ${deviceName} successfully.`
            : 'Firmware logging has been enabled successfully.';
          this.globalService.toastrStatusMessage(
            { ...deviceParameterValues, success: { message } },
            'Enable Firmware Logging',
          );
        } else {
          const message = deviceName
            ? `Failed to enable firmware logging on ${deviceName}.`
            : 'Failed to enable firmware logging.';
          this.globalService.toastrStatusMessage(
            { ...deviceParameterValues, error: { message } },
            'Enable Firmware Logging',
          );
        }
      }),
      map((action) => {
        const { device, deviceParameterValues } = action;
        const parameterValues = (deviceParameterValues.parameterValues ?? []).filter((p) => !!p.success);
        const updates: Update<DeviceParameter>[] = parameterValues.map((parameterValue) => {
          const id = makeDeviceParameterId(device.serialNumber, parameterValue);
          const typeValue = (parameterValue as any).typeValue;
          return {
            changes: { [typeValue]: parameterValue[typeValue], value: parameterValue[typeValue] },
            id,
          };
        });
        return updateDeviceParameters({ updates, initiator: action.type });
      }),
    ),
  );

  handleBulkEnableFirmwareLoggingRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(bulkEnableFirmwareLoggingRequest),
      withLatestFrom(this.store$),
      switchMap(([action, state]) => {
        const requests = action.devices.map((device) => {
          const id = makeDeviceParameterId(device.serialNumber, 0x200e, 1);
          const deviceParameter = state.deviceParameters.entities[id];
          const typeValue = deviceParameter?.typeValue ?? 'int32Value';
          const parameterValues = [{ index: 0x200e, subindex: 1, [typeValue]: 1 }];
          return this.cn.client.request
            .setDeviceParameterValues({ deviceAddress: device.deviceAddress, parameterValues }, 5000)
            .pipe(
              map((deviceParameterValues) => ({ device, deviceParameterValues })),
              catchError(() => {
                const deviceName = this.deviceService.getDeviceName(device);
                return of({
                  device,
                  deviceParameterValues: {
                    timeout: { message: `Timeout while enabling firmware logging on ${deviceName}.` },
                  },
                });
              }),
            );
        });
        return forkJoin(requests).pipe(map((results) => bulkEnableFirmwareLoggingStatus({ results })));
      }),
    ),
  );

  handleBulkEnableFirmwareLoggingStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(bulkEnableFirmwareLoggingStatus),
      tap((action) => {
        const succeeded = action.results.filter((r) =>
          r.deviceParameterValues.parameterValues?.every((p) => !!p.success),
        );
        const failed = action.results.filter(
          (r) => !r.deviceParameterValues.parameterValues?.every((p) => !!p.success),
        );
        if (succeeded.length > 0) {
          const names = succeeded.map((r) => this.deviceService.getDeviceName(r.device)).join(', ');
          this.globalService.toastrStatusMessage(
            { success: { message: `Firmware logging enabled on: ${names}.` } },
            'Bulk Enable Firmware Logging',
          );
        }
        if (failed.length > 0) {
          const names = failed.map((r) => this.deviceService.getDeviceName(r.device)).join(', ');
          this.globalService.toastrStatusMessage(
            { error: { message: `Failed to enable firmware logging on: ${names}.` } },
            'Bulk Enable Firmware Logging',
          );
        }
      }),
      map((action) => {
        const updates: Update<DeviceParameter>[] = action.results.flatMap(({ device, deviceParameterValues }) => {
          const parameterValues = (deviceParameterValues.parameterValues ?? []).filter((p) => !!p.success);
          return parameterValues.map((parameterValue) => {
            const id = makeDeviceParameterId(device.serialNumber, parameterValue);
            const typeValue = (parameterValue as any).typeValue;
            return {
              changes: { [typeValue]: parameterValue[typeValue], value: parameterValue[typeValue] },
              id,
            };
          });
        });
        return updateDeviceParameters({ updates, initiator: action.type });
      }),
    ),
  );

  handleDisableFirmwareLoggingRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(disableFirmwareLoggingRequest),
      withLatestFrom(this.store$),
      mergeMap(([action, state]) => {
        const { device, showDeviceName } = action;
        const id = makeDeviceParameterId(device.serialNumber, 0x200e, 1);
        const deviceParameter = state.deviceParameters.entities[id];
        const typeValue = deviceParameter?.typeValue ?? 'int32Value';
        const parameterValues = [{ index: 0x200e, subindex: 1, [typeValue]: 0 }];
        return this.cn.client.request
          .setDeviceParameterValues({ deviceAddress: device.deviceAddress, parameterValues }, 5000)
          .pipe(
            map((deviceParameterValues) =>
              disableFirmwareLoggingStatus({ device, deviceParameterValues, showDeviceName }),
            ),
            catchError(() => {
              const deviceName = this.deviceService.getDeviceName(device);
              return of(
                disableFirmwareLoggingStatus({
                  device,
                  showDeviceName,
                  deviceParameterValues: {
                    timeout: { message: `Timeout while disabling firmware logging on ${deviceName}.` },
                  },
                }),
              );
            }),
          );
      }),
    ),
  );

  handleDisableFirmwareLoggingStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(disableFirmwareLoggingStatus),
      tap((action) => {
        const { device, deviceParameterValues, showDeviceName } = action;
        const deviceName = showDeviceName ? this.deviceService.getDeviceName(device) : undefined;
        if (deviceParameterValues.parameterValues?.every((p) => !!p.success)) {
          const message = deviceName
            ? `Firmware logging disabled on ${deviceName} successfully.`
            : 'Firmware logging has been disabled successfully.';
          this.globalService.toastrStatusMessage(
            { ...deviceParameterValues, success: { message } },
            'Disable Firmware Logging',
          );
        } else {
          const message = deviceName
            ? `Failed to disable firmware logging on ${deviceName}.`
            : 'Failed to disable firmware logging.';
          this.globalService.toastrStatusMessage(
            { ...deviceParameterValues, error: { message } },
            'Disable Firmware Logging',
          );
        }
      }),
      map((action) => {
        const { device, deviceParameterValues } = action;
        const parameterValues = (deviceParameterValues.parameterValues ?? []).filter((p) => !!p.success);
        const updates: Update<DeviceParameter>[] = parameterValues.map((parameterValue) => {
          const id = makeDeviceParameterId(device.serialNumber, parameterValue);
          const typeValue = (parameterValue as any).typeValue;
          return {
            changes: { [typeValue]: parameterValue[typeValue], value: parameterValue[typeValue] },
            id,
          };
        });
        return updateDeviceParameters({ updates, initiator: action.type });
      }),
    ),
  );

  handleBulkDisableFirmwareLoggingRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(bulkDisableFirmwareLoggingRequest),
      withLatestFrom(this.store$),
      switchMap(([action, state]) => {
        const requests = action.devices.map((device) => {
          const id = makeDeviceParameterId(device.serialNumber, 0x200e, 1);
          const deviceParameter = state.deviceParameters.entities[id];
          const typeValue = deviceParameter?.typeValue ?? 'int32Value';
          const parameterValues = [{ index: 0x200e, subindex: 1, [typeValue]: 0 }];
          return this.cn.client.request
            .setDeviceParameterValues({ deviceAddress: device.deviceAddress, parameterValues }, 5000)
            .pipe(
              map((deviceParameterValues) => ({ device, deviceParameterValues })),
              catchError(() => {
                const deviceName = this.deviceService.getDeviceName(device);
                return of({
                  device,
                  deviceParameterValues: {
                    timeout: { message: `Timeout while disabling firmware logging on ${deviceName}.` },
                  },
                });
              }),
            );
        });
        return forkJoin(requests).pipe(map((results) => bulkDisableFirmwareLoggingStatus({ results })));
      }),
    ),
  );

  handleBulkDisableFirmwareLoggingStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(bulkDisableFirmwareLoggingStatus),
      tap((action) => {
        const succeeded = action.results.filter((r) =>
          r.deviceParameterValues.parameterValues?.every((p) => !!p.success),
        );
        const failed = action.results.filter(
          (r) => !r.deviceParameterValues.parameterValues?.every((p) => !!p.success),
        );
        if (succeeded.length > 0) {
          const names = succeeded.map((r) => this.deviceService.getDeviceName(r.device)).join(', ');
          this.globalService.toastrStatusMessage(
            { success: { message: `Firmware logging disabled on: ${names}.` } },
            'Bulk Disable Firmware Logging',
          );
        }
        if (failed.length > 0) {
          const names = failed.map((r) => this.deviceService.getDeviceName(r.device)).join(', ');
          this.globalService.toastrStatusMessage(
            { error: { message: `Failed to disable firmware logging on: ${names}.` } },
            'Bulk Disable Firmware Logging',
          );
        }
      }),
      map((action) => {
        const updates: Update<DeviceParameter>[] = action.results.flatMap(({ device, deviceParameterValues }) => {
          const parameterValues = (deviceParameterValues.parameterValues ?? []).filter((p) => !!p.success);
          return parameterValues.map((parameterValue) => {
            const id = makeDeviceParameterId(device.serialNumber, parameterValue);
            const typeValue = (parameterValue as any).typeValue;
            return {
              changes: { [typeValue]: parameterValue[typeValue], value: parameterValue[typeValue] },
              id,
            };
          });
        });
        return updateDeviceParameters({ updates, initiator: action.type });
      }),
    ),
  );

  clearDeviceParametersInterfaceUnits$ = createEffect(() =>
    this.actions$.pipe(
      ofType(clearDeviceParametersInterfaceUnits),
      map(({ device }) => {
        const parameterList = Object.values([
          ...unitConverterParameters['position'],
          ...unitConverterParameters['velocity'],
          ...unitConverterParameters['acceleration'],
        ]);
        const parameters = getCurrentValue(this.deviceService.selectDeviceParameters(device, parameterList));

        const updates = parameters.map((deviceParameter) => {
          const changes: Partial<DeviceParameter> = {
            unit: deviceParameter.originalUnit,
          };
          return { changes, id: deviceParameter.id };
        });

        return updateDeviceParameters({ device, updates, initiator: clearDeviceParametersInterfaceUnits.type });
      }),
    ),
  );

  assignFeaturesToParameters$ = createEffect(() =>
    this.actions$.pipe(
      ofType(assignFeaturesToParameters),
      filter(({ type }) => type === assignFeaturesToParameters.type),
      map(({ device }) => {
        const uiFeaturesParameters = getCurrentValue(this.deviceService.selectDeviceUIFeaturesParameters(device));

        const updates = getCurrentValue(this.deviceService.selectDeviceParameters(device)).map((deviceParameter) => {
          let changes: Partial<DeviceParameter> = {};
          const { index, subindex } = deviceParameterIdParts(deviceParameter.id);
          const uiFeatureConfigParameter = uiFeaturesParameters?.find(
            (parameter) => parameter.index === index && parameter.subindex === subindex,
          );

          /**
           * Apply the changes to parameters from UI Features configuration (previously loaded at {@link loadDeviceUIFeaturesConfig$})
           */
          if (uiFeatureConfigParameter) {
            changes.writeAccess = uiFeatureConfigParameter.writeAccess;
          }
          return { changes, id: deviceParameter.id };
        });

        return updateDeviceParameters({ device, updates, initiator: assignFeaturesToParameters.type });
      }),
    ),
  );

  assignParametersUnitsFromUnitConverter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(assignParametersUnitsFromUnitConverter, updateDeviceParameters),
      filter(
        ({ type, initiator }) =>
          type === assignParametersUnitsFromUnitConverter.type || initiator === assignFeaturesToParameters.type,
      ),
      map(({ device }) => {
        const updates = getCurrentValue(this.deviceService.selectDeviceParameters(device)).map((deviceParameter) => {
          let changes: Partial<DeviceParameter> = {};
          const unitConverter = getUnitConverterForParameter(deviceParameter.index, deviceParameter.subindex);

          changes.unitConverterId = unitConverter?.id;

          // Set the default unit for parameters that do not have one (if applicable)
          if (unitConverter?.options[0]?.value) {
            changes.unit = unitConverter.options[0].value;
            changes.originalUnit = changes.unit;
          }

          return { changes, id: deviceParameter.id };
        });

        return updateDeviceParameters({ device, updates, initiator: assignParametersUnitsFromUnitConverter.type });
      }),
    ),
  );

  fetchDeviceInterfaceUnits$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fetchDeviceInterfaceUnit, updateDeviceParameters),
      filter(
        ({ type, initiator }) =>
          type === fetchDeviceInterfaceUnit.type || initiator === assignParametersUnitsFromUnitConverter.type,
      ),
      mergeMap(({ device }) =>
        this.deviceService.selectDeviceUiConfig(device).pipe(
          withLatestFrom(this.deviceService.selectDeviceEncoderConfigurationUsedForPosition(device)),
          map(([uiConfig, encoderConfiguration]) => {
            const { interfaceUnit } = uiConfig ?? {};
            let { unit, feed } = interfaceUnit ?? {};

            if (feed === encoderConfiguration?.resolution) {
              unit = 'inc';
            } else if (feed === 360) {
              unit = 'deg';
            } else if (feed === 1) {
              unit = 'rev';
            }

            const UIUnit = {
              unit,
              feed,
            };

            return setDeviceParametersInterfaceUnitsFromUiConfig({ device, interfaceUnit: UIUnit });
          }),
        ),
      ),
    ),
  );

  setDeviceParametersInterfaceUnitsFromUiConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setDeviceParametersInterfaceUnitsFromUiConfig),
      map(({ device, interfaceUnit }) => {
        const parameterList = Object.values([
          ...unitConverterParameters['position'],
          ...unitConverterParameters['velocity'],
          ...unitConverterParameters['acceleration'],
        ]);
        const parameters = getCurrentValue(this.deviceService.selectDeviceParameters(device, parameterList));

        const updates: Update<DeviceParameter>[] = parameters.map((parameter) => {
          const unit =
            getInterfaceUnitFromUnitConverter(interfaceUnit, parameter.unitConverterId) ?? parameter.originalUnit;
          return {
            changes: {
              unit,
              interfaceUnit: interfaceUnit?.unit,
            },
            id: parameter.id,
          };
        });

        return updateDeviceParameters({
          updates,
          device,
          initiator: setDeviceParametersInterfaceUnitsFromUiConfig.type,
        });
      }),
    ),
  );

  dispatchReloadDeviceStateMachineRequestOnSetDeviceParameterValuesStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setDeviceParameterValuesStatus),
      filter((action) => {
        if (action.deviceParameterValues?.parameterValues) {
          const map = this.deviceService.selectReloadDeviceStateMachineParametersMap(action.device);
          return action.deviceParameterValues.parameterValues.some((p) => {
            if (map.has(p.index)) {
              const entry = map.get(p.index);
              return Array.isArray(entry) ? entry.includes(p.subindex) : true;
            } else {
              return false;
            }
          });
        } else {
          console.warn('No device parameter values in set device parameter values status.');
          return false;
        }
      }),
      map((action) => {
        const { device } = action;

        const hasFault = getCurrentValue(this.deviceService.selectDeviceHasFault(device));

        if (hasFault) {
          return resetDeviceFaultRequest({
            device,
            suppressToastr: {
              error: true,
              info: true,
              begin: true,
              success: false,
              timeout: true,
              warning: true,
            },
            timeout: 5000,
          });
        } else {
          return reloadDeviceStateMachineRequest({
            device,
            suppressToastr: { begin: true, success: true },
            timeout: 5000,
          });
        }
      }),
    ),
  );
}
